import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useAuth } from './AuthContext'

const TimezoneContext = createContext(null)

// Common US timezones for reference
export const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Phoenix', label: 'Arizona (no DST)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' }
]

export function TimezoneProvider({ children }) {
  const { isAuthenticated, getAccessToken } = useAuth()
  const [timezone, setTimezone] = useState('America/Chicago')
  const [loading, setLoading] = useState(true)

  // Load timezone from department settings.
  //
  // Settings reads need a token, and this ran without one -- so once
  // /api/settings stopped answering anonymous callers, every department silently
  // fell back to America/Chicago. It waits for authentication and re-runs when
  // that arrives, because this provider mounts before anyone has signed in.
  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false)
      return
    }
    const loadTimezone = async () => {
      try {
        const token = getAccessToken()
        const res = await fetch('/api/settings/department_info', {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        })
        const data = await res.json()
        if (data.success && data.setting?.value?.timezone) {
          setTimezone(data.setting.value.timezone)
        }
      } catch (err) {
        console.error('Failed to load timezone:', err)
      } finally {
        setLoading(false)
      }
    }
    loadTimezone()
  }, [isAuthenticated, getAccessToken])

  // Format a date in the department's timezone
  const formatDate = useCallback((date, options = {}) => {
    if (!date) return '—'
    // For date-only strings (YYYY-MM-DD), append T12:00:00 to avoid UTC midnight timezone shift
    const parsed = typeof date === 'string'
      ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(date) ? date + 'T12:00:00' : date)
      : date
    const d = parsed
    if (isNaN(d.getTime())) return '—'
    
    const defaultOptions = {
      timeZone: timezone,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      ...options
    }
    
    return d.toLocaleDateString('en-US', defaultOptions)
  }, [timezone])

  // Format a time in the department's timezone
  const formatTime = useCallback((date, options = {}) => {
    if (!date) return '—'
    const d = typeof date === 'string' ? new Date(date) : date
    if (isNaN(d.getTime())) return '—'
    
    const defaultOptions = {
      timeZone: timezone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      ...options
    }
    
    return d.toLocaleTimeString('en-US', defaultOptions)
  }, [timezone])

  // Format a datetime in the department's timezone
  const formatDateTime = useCallback((date, options = {}) => {
    if (!date) return '—'
    const d = typeof date === 'string' ? new Date(date) : date
    if (isNaN(d.getTime())) return '—'
    
    const defaultOptions = {
      timeZone: timezone,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      ...options
    }
    
    return d.toLocaleString('en-US', defaultOptions)
  }, [timezone])

  // Format relative time (e.g., "2 hours ago")
  const formatRelative = useCallback((date) => {
    if (!date) return '—'
    const d = typeof date === 'string' ? new Date(date) : date
    if (isNaN(d.getTime())) return '—'
    
    const now = new Date()
    const diffMs = now - d
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)
    
    if (diffMins < 1) return 'just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    
    return formatDate(d)
  }, [formatDate])

  // Get current date in timezone (useful for date inputs)
  const getCurrentDate = useCallback(() => {
    const now = new Date()
    // Get the date string in the timezone
    const dateStr = now.toLocaleDateString('en-CA', { timeZone: timezone }) // en-CA gives YYYY-MM-DD format
    return dateStr
  }, [timezone])

  // Get current datetime in timezone
  const getCurrentDateTime = useCallback(() => {
    return new Date().toLocaleString('en-US', { timeZone: timezone })
  }, [timezone])

  // Convert a local date input value to ISO string
  const toISOString = useCallback((dateStr, timeStr = '00:00') => {
    if (!dateStr) return null
    // Create date in the timezone context
    const [year, month, day] = dateStr.split('-').map(Number)
    const [hours, minutes] = timeStr.split(':').map(Number)
    
    // Create a Date object - this will be in local time
    const date = new Date(year, month - 1, day, hours, minutes)
    return date.toISOString()
  }, [])

  // Get timezone label
  const getTimezoneLabel = useCallback(() => {
    const tz = TIMEZONES.find(t => t.value === timezone)
    return tz?.label || timezone
  }, [timezone])

  const value = {
    timezone,
    loading,
    formatDate,
    formatTime,
    formatDateTime,
    formatRelative,
    getCurrentDate,
    getCurrentDateTime,
    toISOString,
    getTimezoneLabel,
    TIMEZONES
  }

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  )
}

export function useTimezone() {
  const context = useContext(TimezoneContext)
  if (!context) {
    throw new Error('useTimezone must be used within a TimezoneProvider')
  }
  return context
}

export default TimezoneContext
